import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Chip,
  FieldError,
  Label,
  ListBox,
  SearchField,
  Spinner,
} from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { ConfirmDialog } from '@/components/ui/form-modal'
import { FormSection, FormSectionEmpty } from '@/components/ui/form-section'
import { CheckCircleIcon, LinkIcon } from '@/components/ui/icons'
import type { TenantBranch } from '@/features/business'
import { errorMessage } from '@/lib/api/api-error'

import { BRANCH_PAGE_SIZE, BRANCH_SEARCH_DEBOUNCE_MS, isBranchKnown } from '../branch-links'
import { useDebouncedValue, useProductActions, useProducts } from '../hooks'
import { ProductType, translated } from '../types'
import type { ProductBranchSummary, ProductListQuery, ProductSummary } from '../types'

import { BranchProductSearch } from './branch-product-parts'

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

interface BranchProductEditorProps {
  /** Every branch the tenant has. Any number — the list scrolls and searches. */
  branches: TenantBranch[]
}

/**
 * One product at a time: pick it on the left, tick the branches that sell it on
 * the right, and save.
 *
 * The other views write each tick as it happens. This one holds the ticks as a
 * draft and saves them together, because it is where a merchant reworks one
 * product's whole list — and a list that is half-saved while they are still
 * deciding is a till selling the wrong thing in the meantime. The draft shows
 * what will change ("New", "Removing") before anything does.
 *
 * Branches the product keeps keep their stock count; only the ones dropped lose
 * theirs, which the save asks about first for a stockable product.
 */
export function BranchProductEditor({ branches }: BranchProductEditorProps) {
  const [search, setSearch] = useState('')
  const searchText = useDebouncedValue(search.trim(), BRANCH_SEARCH_DEBOUNCE_MS)
  const [page, setPage] = useState(0)

  // A new search can strand the merchant on a page that no longer exists.
  // Reset during render rather than in an effect, so no frame shows it.
  const [lastSearchText, setLastSearchText] = useState(searchText)

  if (searchText !== lastSearchText) {
    setLastSearchText(searchText)
    setPage(0)
  }

  const query = useMemo<ProductListQuery>(
    () => ({
      limit: BRANCH_PAGE_SIZE,
      offset: page * BRANCH_PAGE_SIZE,
      searchText: searchText || undefined,
    }),
    [page, searchText],
  )

  const { products, total, isLoading, isRefreshing, error, retry } = useProducts(query)
  const { setBranches } = useProductActions()

  /*
   * The product being edited.
   *
   * Until the merchant picks one it is simply the first row, so the right-hand
   * side is never empty. Once picked — or once its first box is ticked — it is
   * pinned, and read through the list when the list has it so a save's refetch
   * reaches the editor; the pinned copy covers a product paged out of view.
   */
  const [picked, setPicked] = useState<ProductSummary | null>(null)
  const product = (picked && products.find((row) => row.id === picked.id)) ?? picked ?? products[0]

  /** The ticks not yet saved. `null` is "no edits" — the product's saved list. */
  const [draft, setDraft] = useState<string[] | null>(null)
  const [branchSearch, setBranchSearch] = useState('')
  const [pendingSwitch, setPendingSwitch] = useState<ProductSummary | null>(null)
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false)

  const isKnown = product ? isBranchKnown(product) : false
  const savedIds = (product?.branches ?? []).map((branch) => branch.id)
  const chosenIds = draft ?? savedIds
  const chosen = new Set(chosenIds)
  const saved = new Set(savedIds)

  const added = branches.filter((branch) => chosen.has(branch.id) && !saved.has(branch.id))
  const removed = branches.filter((branch) => saved.has(branch.id) && !chosen.has(branch.id))
  const isDirty = added.length > 0 || removed.length > 0

  const needle = branchSearch.trim().toLowerCase()
  const visibleBranches = needle
    ? branches.filter((branch) =>
        [branch.name?.en, branch.name?.km, branch.address?.en, branch.address?.km].some((text) =>
          text?.toLowerCase().includes(needle),
        ),
      )
    : branches
  const isAllVisibleChosen =
    visibleBranches.length > 0 && visibleBranches.every((branch) => chosen.has(branch.id))

  function choose(next: ProductSummary) {
    if (next.id === product?.id) return

    // Moving on would throw the ticks away without a word.
    if (isDirty) {
      setPendingSwitch(next)
      return
    }

    switchTo(next)
  }

  function switchTo(next: ProductSummary) {
    setPicked(next)
    setDraft(null)
    setBranchSearch('')
    setBranches.reset()
  }

  function updateDraft(ids: string[]) {
    // Pin it: until now it may only have been the page's first row, and a page
    // turn would otherwise swap a different product in under the edit.
    if (!picked && product) setPicked(product)
    setDraft(ids)
  }

  /** "Select all" and "Clear" act on the branches the search is showing. */
  function toggleAllVisible() {
    const ids = visibleBranches.map((branch) => branch.id)

    updateDraft(
      isAllVisibleChosen
        ? chosenIds.filter((id) => !ids.includes(id))
        : [...new Set([...chosenIds, ...ids])],
    )
  }

  function save() {
    if (!product || !isDirty || chosenIds.length === 0) return

    if (removed.length > 0 && product.type === ProductType.Stockable) {
      setIsConfirmingRemoval(true)
      return
    }

    commit()
  }

  function commit() {
    if (!product) return

    const target = product
    // In the branch list's own order, so the chips read the same as the boxes.
    const ids = branches.filter((branch) => chosen.has(branch.id)).map((branch) => branch.id)

    setBranches.mutate(
      { id: target.id, tenantBranchIds: ids },
      {
        onSuccess: () => {
          // The pinned copy carries the saved list too, for when this product is
          // not on the page the refetch brought back.
          const refs: ProductBranchSummary[] = branches
            .filter((branch) => ids.includes(branch.id))
            .map((branch) => ({ id: branch.id, name: branch.name }))

          setPicked({ ...target, branches: refs })
          setDraft(null)
        },
      },
    )
  }

  const productName = product ? translated(product.name) : ''
  const removedNames = removed.map((branch) => translated(branch.name, 'Branch'))

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState subject="your products" error={error} onRetry={retry} />

  if (!product && !searchText) {
    return (
      <EmptyState
        title="No products yet."
        description="Add products on the Products tab, then choose here which branches sell each one."
      />
    )
  }

  return (
    <>
      <ModuleIntro description="Pick a product, tick the branches that sell it, and save. Branches it keeps keep their stock." />

      {/* `brand-soft-scope` makes the selected row and the summary card brand
          purple rather than HeroUI's blue — see globals.css. */}
      <div className="brand-soft-scope grid items-start gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <FormSection
          title="Products"
          description={`${total} ${total === 1 ? 'product' : 'products'}`}
          className="bg-surface"
        >
          <div className="flex flex-col gap-3">
            <BranchProductSearch value={search} onChange={setSearch} />

            {products.length === 0 ? (
              <FormSectionEmpty>No product matches “{searchText}”.</FormSectionEmpty>
            ) : (
              <ListBox
                aria-label="Products"
                selectionMode="single"
                disallowEmptySelection
                selectedKeys={product ? [product.id] : []}
                onSelectionChange={(keys) => {
                  const [key] = keys as Set<string>
                  const next = products.find((row) => row.id === key)
                  if (next) choose(next)
                }}
                className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}
              >
                {products.map((row) => (
                  <ListBox.Item
                    key={row.id}
                    id={row.id}
                    textValue={translated(row.name)}
                    className="data-[selected=true]:bg-accent-soft rounded-xl px-2 py-2"
                  >
                    <ProductOption
                      product={row}
                      branchCount={branches.length}
                      isSelected={row.id === product?.id}
                    />
                  </ListBox.Item>
                ))}
              </ListBox>
            )}

            <Pagination
              page={page}
              pageSize={BRANCH_PAGE_SIZE}
              total={total}
              onPageChange={setPage}
            />
          </div>
        </FormSection>

        {product && (
          <div className="flex flex-col gap-4">
            <FormSection
              title="Select branches"
              description={`Where ${productName} is sold.`}
              className="bg-surface"
              action={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-accent h-8 rounded-full px-3 text-xs font-medium"
                  isDisabled={!isKnown || visibleBranches.length === 0 || setBranches.isPending}
                  onPress={toggleAllVisible}
                >
                  {isAllVisibleChosen ? 'Clear' : 'Select all'}
                </Button>
              }
            >
              <div className="flex flex-col gap-3">
                <SearchField
                  value={branchSearch}
                  onChange={setBranchSearch}
                  aria-label="Search branches"
                  className="flex flex-col"
                >
                  <SearchField.Group>
                    <SearchField.SearchIcon />
                    <SearchField.Input placeholder="Search branch…" />
                    <SearchField.ClearButton />
                  </SearchField.Group>
                </SearchField>

                {!isKnown ? (
                  <FormSectionEmpty>
                    This product’s branches did not load. Reload the page and try again.
                  </FormSectionEmpty>
                ) : (
                  <CheckboxGroup
                    value={chosenIds}
                    onChange={updateDraft}
                    isInvalid={chosenIds.length === 0}
                    isDisabled={setBranches.isPending}
                    className="flex flex-col gap-2"
                  >
                    <Label className="sr-only">Branches that sell {productName}</Label>

                    {visibleBranches.length === 0 ? (
                      <FormSectionEmpty>
                        No branch matches “{branchSearch.trim()}”.
                      </FormSectionEmpty>
                    ) : (
                      <div className="flex max-h-72 flex-col gap-1 overflow-y-auto">
                        {visibleBranches.map((branch) => (
                          <BranchOption
                            key={branch.id}
                            branch={branch}
                            change={
                              chosen.has(branch.id) && !saved.has(branch.id)
                                ? 'adding'
                                : saved.has(branch.id) && !chosen.has(branch.id)
                                  ? 'removing'
                                  : null
                            }
                          />
                        ))}
                      </div>
                    )}

                    <FieldError>
                      Choose at least one branch — a product has to be sold somewhere.
                    </FieldError>
                  </CheckboxGroup>
                )}
              </div>
            </FormSection>

            <SelectedProductCard
              product={product}
              chosen={branches.filter((branch) => chosen.has(branch.id))}
              branchCount={branches.length}
            >
              {/* Left: what saving would do, or that there is nothing to save —
                  or why the last save failed, beside the button that retries it. */}
              <p
                role={setBranches.error != null ? 'alert' : undefined}
                className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-xs"
              >
                {setBranches.error != null ? (
                  <span className="text-danger">
                    {errorMessage(
                      setBranches.error,
                      'Could not save these branches. Please try again.',
                    )}
                  </span>
                ) : isDirty ? (
                  <>
                    {added.length > 0 && (
                      <span className="text-success">
                        + {added.map((branch) => translated(branch.name, 'Branch')).join(', ')}
                      </span>
                    )}
                    {removed.length > 0 && (
                      <span className="text-danger">− {removedNames.join(', ')}</span>
                    )}
                  </>
                ) : (
                  <span className="text-muted inline-flex items-center gap-1.5">
                    <CheckCircleIcon width={14} height={14} className="text-success" />
                    Up to date
                  </span>
                )}
              </p>

              <span className="flex shrink-0 items-center gap-2">
                {isDirty && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted h-9 rounded-full px-3 text-xs"
                    isDisabled={setBranches.isPending}
                    onPress={() => setDraft(null)}
                  >
                    Undo
                  </Button>
                )}

                <Button
                  variant="primary"
                  size="sm"
                  className="h-9 rounded-full px-4 font-medium"
                  isDisabled={!isDirty || chosenIds.length === 0 || setBranches.isPending}
                  onPress={save}
                >
                  {setBranches.isPending ? (
                    <Spinner size="sm" />
                  ) : (
                    <LinkIcon width={14} height={14} />
                  )}
                  {saveLabel({
                    isPending: setBranches.isPending,
                    isDirty,
                    added: added.length,
                    removed: removed.length,
                  })}
                </Button>
              </span>
            </SelectedProductCard>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={isConfirmingRemoval}
        onOpenChange={setIsConfirmingRemoval}
        title={`Stop selling ${productName} at ${removedNames.join(', ') || 'these branches'}?`}
        body={`The stock counted there is cleared. Adding ${
          removed.length === 1 ? 'that branch' : 'them'
        } back later starts from zero. The branches ${productName} keeps are not touched.`}
        confirmLabel="Save and clear stock"
        onConfirm={() => {
          setIsConfirmingRemoval(false)
          commit()
        }}
        isBusy={false}
      />

      <ConfirmDialog
        isOpen={pendingSwitch !== null}
        onOpenChange={(isOpen) => !isOpen && setPendingSwitch(null)}
        title="Discard unsaved changes?"
        body={`The branches you ticked for ${productName} have not been saved.`}
        confirmLabel="Discard changes"
        onConfirm={() => {
          if (pendingSwitch) switchTo(pendingSwitch)
          setPendingSwitch(null)
        }}
        isBusy={false}
      />
    </>
  )
}

/** What the save button says it will do. */
function saveLabel({
  isPending,
  isDirty,
  added,
  removed,
}: {
  isPending: boolean
  isDirty: boolean
  added: number
  removed: number
}): string {
  if (isPending) return 'Saving…'
  if (!isDirty) return 'Save'
  if (removed === 0) return `Link to ${added} ${added === 1 ? 'branch' : 'branches'}`
  return 'Save changes'
}

/**
 * A row of the product list: picture, name and SKU, how many branches sell it,
 * and a tick on the one being edited. The tick is drawn here rather than by
 * `ListBox.ItemIndicator`, which floats over the end of the row where the
 * count sits.
 */
function ProductOption({
  product,
  branchCount,
  isSelected,
}: {
  product: ProductSummary
  branchCount: number
  isSelected: boolean
}) {
  const count = product.branches?.length

  return (
    <span className="flex min-w-0 flex-1 items-center gap-3">
      {product.thumbnail?.url ? (
        <img
          src={product.thumbnail.url}
          alt=""
          className="border-border size-10 shrink-0 rounded-lg border object-cover"
        />
      ) : (
        <span className="border-border bg-surface size-10 shrink-0 rounded-lg border" />
      )}

      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{translated(product.name)}</span>
        <span className="text-muted truncate text-xs">{product.sku || 'No SKU'}</span>
      </span>

      {count === 0 ? (
        <Chip size="sm" color="warning" variant="soft">
          No branch
        </Chip>
      ) : (
        count !== undefined && (
          <span className="text-muted shrink-0 text-xs tabular-nums">
            {count} / {branchCount}
          </span>
        )
      )}

      {isSelected && <CheckCircleIcon width={18} height={18} className="text-accent shrink-0" />}
    </span>
  )
}

/**
 * One branch's box, with its address and — while the draft differs from what
 * is saved — whether ticking it is adding the branch or unticking it removing it.
 */
function BranchOption({
  branch,
  change,
}: {
  branch: TenantBranch
  change: 'adding' | 'removing' | null
}) {
  const address = translated(branch.address, '')

  return (
    // `mt-0`: HeroUI's group pushes every box down 16px, which suits a short
    // form field but spreads a list of branches out to a page and a half.
    <Checkbox value={branch.id} className="hover:bg-default mt-0 rounded-xl px-2 py-1.5">
      <Checkbox.Content className="w-full items-start">
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>

        {/* Plain text rather than the Label and Description slots: inside a
            checkbox those carry the field's own spacing, which doubled the
            height of every row. The content is a <label>, so this text is
            still the box's accessible name. */}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium">{translated(branch.name, 'Branch')}</span>
          {address && <span className="text-muted truncate text-xs font-normal">{address}</span>}
        </span>

        {change === 'adding' && (
          <Chip size="sm" color="success" variant="soft">
            New
          </Chip>
        )}
        {change === 'removing' && (
          <Chip size="sm" color="danger" variant="soft">
            Removing
          </Chip>
        )}
      </Checkbox.Content>
    </Checkbox>
  )
}

/**
 * What is being edited, where it will be sold, and the save — one card.
 *
 * A hairline card on the surface, like the two above it, rather than a filled
 * block: the tint made the summary the loudest thing on the screen, and the
 * full-width button under it read as a disabled bar whenever there was nothing
 * to save, which is most of the time. The save sits in the card's footer at the
 * size of every other primary button in the Back Office.
 */
function SelectedProductCard({
  product,
  chosen,
  branchCount,
  children,
}: {
  product: ProductSummary
  chosen: TenantBranch[]
  branchCount: number
  /** The footer — save status on the left, actions on the right. */
  children: ReactNode
}) {
  const variants = product.children?.length ?? 0

  return (
    <section className="border-border bg-surface overflow-hidden rounded-2xl border">
      <div className="flex flex-wrap items-center gap-4 p-4 sm:p-5">
        {product.thumbnail?.url ? (
          <img
            src={product.thumbnail.url}
            alt=""
            className="border-border size-12 shrink-0 rounded-xl border object-cover"
          />
        ) : (
          <span className="border-border bg-default size-12 shrink-0 rounded-xl border" />
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-accent text-[11px] font-medium tracking-wide uppercase">
            Selected product
          </span>
          <span className="truncate font-semibold">{translated(product.name)}</span>
          <span className="text-muted truncate text-xs">
            {product.sku || 'No SKU'} · {money.format(product.price ?? 0)}
            {variants > 0 && ` · ${variants} variants follow it`}
          </span>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <span className="text-muted text-xs tabular-nums">
            <span className="text-foreground font-medium">{chosen.length}</span> of {branchCount}{' '}
            {branchCount === 1 ? 'branch' : 'branches'}
          </span>
          <span className="flex flex-wrap justify-end gap-1">
            {chosen.length === 0 ? (
              <Chip size="sm" color="warning" variant="soft">
                None
              </Chip>
            ) : (
              <>
                {chosen.slice(0, 3).map((branch) => (
                  <Chip key={branch.id} size="sm" color="success" variant="soft">
                    {translated(branch.name, 'Branch')}
                  </Chip>
                ))}
                {chosen.length > 3 && (
                  <Chip size="sm" variant="soft">
                    +{chosen.length - 3}
                  </Chip>
                )}
              </>
            )}
          </span>
        </div>
      </div>

      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 sm:px-5">
        {children}
      </div>
    </section>
  )
}
